import { MbscResource } from '@akoom-tech/mobiscroll-react';
import { Box, Stack, Typography } from '@mui/material';
import { providersPath } from '@/features/users/providers/routes/paths';
import TableRowLink from '@/components/table/TableRowLink';
//
type SchedulerResource = MbscResource & {
  first_name?: string;
  last_name?: string;
  isHighlighted?: boolean;
  schedulerAssignedHours?: number;
  schedulerUtilisation?: number;
};

type MbscCleanerCellProps = {
  cleanerResource: SchedulerResource;
};

const formatScheduledHours = (hours?: number) => {
  const value = Number.isFinite(hours) ? Number(hours) : 0;
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1);
};

const normaliseUtilisation = (value?: number) => {
  const utilisation = Number.isFinite(value) ? Number(value) : 0;
  return Math.max(0, utilisation);
};

export default function MbscCleanerCell({
  cleanerResource,
}: MbscCleanerCellProps) {
  const providerName =
    `${cleanerResource.first_name ?? ''} ${
      cleanerResource.last_name ?? ''
    }`.trim() || 'Cleaner';
  const providerLink =
    cleanerResource.id != null
      ? providersPath('edit', {
          userId: String(cleanerResource.id),
          tabSid: 'details',
        })
      : null;
  const isHighlighted = Boolean(cleanerResource.isHighlighted);

  return (
    <Stack
      spacing={1}
      data-testid={isHighlighted ? 'scheduler-highlighted-cleaner' : undefined}
      data-scheduler-highlighted-cleaner={isHighlighted ? 'true' : undefined}
      sx={{
        width: '100%',
        height: '100%',
        minWidth: 0,
        justifyContent: 'center',
        py: 0.4,
      }}
    >
      {providerLink ? (
        <TableRowLink
          linkText={providerName}
          linkTo={providerLink}
          noWrap
          sx={{
            px: 0,
            py: 0,
            fontSize: 15,
            fontWeight: 800,
            lineHeight: 1.2,
            color: 'text.primary',
            '&:link': {
              color: 'text.primary',
            },
            '&:visited': {
              color: 'text.primary',
            },
            '&:active': {
              color: 'text.primary',
            },
          }}
        />
      ) : (
        <Typography fontSize={15} fontWeight={800} lineHeight={1.2} noWrap>
          {providerName}
        </Typography>
      )}
      <UtilisationStats cleanerResource={cleanerResource} />
    </Stack>
  );
}

function UtilisationStats({
  cleanerResource,
}: {
  cleanerResource: SchedulerResource;
}) {
  const utilisation = normaliseUtilisation(
    cleanerResource.schedulerUtilisation
  );
  const bookedPercentage = Math.round(utilisation);
  const progressPercentage = Math.min(100, utilisation);
  const isUnderBooked = utilisation < 40;
  const scheduledHours = formatScheduledHours(
    cleanerResource.schedulerAssignedHours
  );
  const bookedLabel = `${bookedPercentage}% booked (${scheduledHours}h)`;

  return (
    <Stack spacing={0.35} sx={{ width: '100%', minWidth: 0 }}>
      <Typography fontSize={12.5} color="text.secondary" lineHeight={1.15}>
        {bookedLabel}
      </Typography>
      <Box
        role="progressbar"
        aria-label={bookedLabel}
        aria-valuemin={0}
        aria-valuemax={Math.max(100, bookedPercentage)}
        aria-valuenow={bookedPercentage}
        data-utilization-tone={isUnderBooked ? 'underbooked' : 'normal'}
        sx={{
          mt: 0.1,
          width: '100%',
          height: 4,
          borderRadius: 999,
          backgroundColor: 'grey.300',
          overflow: 'hidden',
        }}
      >
        <Box
          data-testid="scheduler-utilization-fill"
          sx={{
            width: `${progressPercentage}%`,
            height: '100%',
            borderRadius: 'inherit',
            backgroundColor: isUnderBooked ? '#F59E0B' : '#2F6FED',
          }}
        />
      </Box>
    </Stack>
  );
}
